// What to do when a route refuses to open. Lifted from go-schedule's
// src/lib/navigationRecovery.ts (mechanism as-is; only the sessionStorage key
// is renamed for this app).
//
// The failure this exists for: every page in the module registry is a lazy
// `() => import('src/modules/…')`, and the built chunks are content-hashed
// and embedded in the Go binary. A deploy therefore replaces the whole asset
// set while open tabs are still running the previous build — and a tab that
// has been open across a deploy is asking for chunk names that no longer
// exist. The first route the user has not visited yet fails to load,
// `router.push` rejects, the route never changes, and the app just sits
// there. Every further unvisited route fails the same way, so from the
// user's chair navigation is simply dead until they reload by hand, with
// nothing on screen saying so.
//
// The cure is the reload they would have done themselves, aimed at the route
// they asked for, so the click appears to have worked. The guard below stops
// that becoming a loop when the reload cannot fix it (a genuinely missing
// asset, a proxy serving garbage): a second failure for the same path inside
// the window is reported instead of reloaded again.

import type { Router } from 'vue-router'

/** Where the "we already tried reloading for this" note is kept — sessionStorage
 *  in the browser, an object in tests. Deliberately per-tab: another tab's
 *  reload says nothing about this one. */
export interface ReloadMemory {
  read(): { path: string; at: number } | null
  write(entry: { path: string; at: number }): void
}

/** Long enough to cover the reload itself (fetch index.html, boot, re-navigate),
 *  short enough that a stale note never suppresses a real recovery later in the
 *  session. */
export const RELOAD_GUARD_MS = 30_000

// The browsers disagree on the wording, and the wording is all we get: a failed
// dynamic import surfaces as a plain TypeError with no code, no cause and no
// status. Both Chromium strings are here because both are known to appear in
// this kind of project's error tracking ("Failed to fetch dynamically imported
// module" and "error loading dynamically imported module"), and they are not
// the same code path — the first is the fetch failing, the second the module
// graph failing to instantiate, which is what a stale chunk served as
// index.html produces.
const STALE_CHUNK_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  // Chromium's other wording, and Firefox's only one.
  /error loading dynamically imported module/i,
  // Safari
  /importing a module script failed/i,
  // Vite's own preload helper, for the CSS of a lazy route
  /unable to preload css/i,
  // webpack-flavoured wording, in case a dependency's chunk loader reports it
  /loading chunk \S+ failed/i,
  /loading css chunk \S+ failed/i,
]

/**
 * Whether this navigation error is "the build moved under us".
 *
 * Kept a message match on purpose. It is tempting to treat every TypeError
 * from a navigation as a stale chunk and reload, but that would turn an
 * ordinary programming error in a route guard into a reload loop dressed up
 * as a network problem — the one failure mode this file exists to prevent.
 */
export function isStaleChunkError(err: unknown): boolean {
  const message =
    err instanceof Error ? err.message : typeof err === 'string' ? err : String(err ?? '')
  return STALE_CHUNK_PATTERNS.some((re) => re.test(message))
}

/**
 * `reload` to fetch the new build and land on `path`, `report` to give up and
 * tell Sentry. Pure, so the decision is testable without a browser.
 */
export function decideNavigationRecovery(
  err: unknown,
  path: string,
  memory: ReloadMemory,
  now: number,
): 'reload' | 'report' {
  if (!isStaleChunkError(err)) return 'report'
  const last = memory.read()
  // Only the same path suppresses: a reload that fixed the route the user
  // asked for and then failed on a different one is a second real deploy,
  // not a loop.
  if (last && last.path === path && now - last.at < RELOAD_GUARD_MS) return 'report'
  memory.write({ path, at: now })
  return 'reload'
}

export interface NavigationErrorDeps {
  memory: ReloadMemory
  now(): number
  /** Full page load of `href`. Injected because a test cannot navigate. */
  reload(href: string): void
  report(err: unknown): void
  notify(message: string): void
}

/**
 * The single place a navigation error is dealt with.
 *
 * `router.onError` and not a `try`/`catch` at the call sites: a route can
 * also be entered from a `<router-link>`, from the address bar, or from the
 * back button, and none of those has a call site to wrap.
 */
export function installNavigationErrorHandler(router: Router, deps: NavigationErrorDeps): void {
  router.onError((err, to) => {
    // `href`, not `fullPath`: the router may be mounted under a base path,
    // and reloading a path that omits it would land on the server's 404.
    const href = router.resolve(to).href
    if (decideNavigationRecovery(err, to.fullPath, deps.memory, deps.now()) === 'reload') {
      deps.reload(href)
      return
    }
    deps.report(err)
    // The route did not change, so without this the user is left looking at
    // the page they were already on with nothing to say the click did
    // anything.
    deps.notify('Could not open that page. Reload if it keeps happening.')
  })
}

/**
 * For `.catch()` on a programmatic navigation.
 *
 * vue-router rejects a `push`/`replace` for two quite different reasons, and
 * neither wants handling at the call site. A thrown error has already been
 * through the `router.onError` handler above, which vue-router runs and then
 * rejects the promise anyway. A *navigation failure* — the redundant
 * navigation a watcher-driven `replace` produces when the query it computed
 * is the one already in the address bar — vue-router resolves rather than
 * rejects, so it never reaches here at all. What is left over is an
 * unhandled rejection: noise in error tracking, and a red console.
 *
 * Deliberately in this module rather than beside the handler: a no-op should
 * not drag @sentry/vue and Quasar's Notify into every page that navigates.
 */
export function ignoreNavigationError(): void {
  /* already handled by router.onError */
}

const MEMORY_KEY = 'portal:navigation-reload'

export function sessionReloadMemory(storage: Storage): ReloadMemory {
  return {
    read() {
      try {
        const raw = storage.getItem(MEMORY_KEY)
        if (!raw) return null
        const parsed: unknown = JSON.parse(raw)
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          typeof (parsed as { path?: unknown }).path === 'string' &&
          typeof (parsed as { at?: unknown }).at === 'number'
        ) {
          return parsed as { path: string; at: number }
        }
        return null
      } catch {
        // A storage that throws (private mode, quota, disabled) must not be
        // the reason a navigation error goes unhandled. No memory means
        // "reload", which is the behaviour without this guard at all.
        return null
      }
    },
    write(entry) {
      try {
        storage.setItem(MEMORY_KEY, JSON.stringify(entry))
      } catch {
        /* see above */
      }
    },
  }
}
